import { numberToPrettyString, plural } from '@cian/utils';

import { NBSP } from 'shared/constants/text';
import { formatNumber, formatRubles, noBreak } from 'shared/utils/format';
import type { ICategoryOffer, TOffersDealType } from 'shared/types/buildingOffers';

/** Сокращать цену продажи в таблице, начиная с этой суммы (`utils/offer.ts` монолита) */
const CUT_PRICE_FROM = 500_000;

const isPositive = (value: number | null): value is number => value !== null && value > 0;

/**
 * Ставка аренды за м² в год из цены за месяц — как `PriceHelper` монолита (округление вверх)
 */
export function getRentRatePerYear({ price, totalArea }: Pick<ICategoryOffer, 'price' | 'totalArea'>): number | null {
  return isPositive(price) && isPositive(totalArea) ? Math.ceil((price * 12) / totalArea) : null;
}

/** Цена продажи за м² — как `PriceHelper` монолита (округление вверх) */
export function getSalePricePerMeter({ price, totalArea }: Pick<ICategoryOffer, 'price' | 'totalArea'>): number | null {
  return isPositive(price) && isPositive(totalArea) ? Math.ceil(price / totalArea) : null;
}

/** Сокращённая цена: «850 тыс. руб.», «63,9 млн. руб.» (`prettyCutPriceString` монолита — десятые без округления) */
export function formatCutPrice(price: number): string {
  if (price < 1000) {
    return `${price}${NBSP}руб.`;
  }

  if (price < 1_000_000) {
    return `${Math.trunc(price / 1000)}${NBSP}тыс.${NBSP}руб.`;
  }

  const [integer, fractional] = String(price / 1_000_000).split('.');
  const millions = noBreak(formatNumber(Number(integer)));
  const tenths = fractional?.[0];

  return `${tenths ? `${millions},${tenths}` : millions}${NBSP}млн.${NBSP}руб.`;
}

/** Ячейка «Этаж»: «3 / 10», «3» или «-» (`prettyFloorString` монолита: нулевой этаж — как отсутствующий) */
export function formatOfferFloor({ floorNumber, floorsCount }: Pick<ICategoryOffer, 'floorNumber' | 'floorsCount'>) {
  if (!floorNumber) {
    return '-';
  }

  return floorsCount ? `${floorNumber} / ${floorsCount}` : String(floorNumber);
}

/** Ячейка цены: аренда — ставка за м² в год, продажа — цена за объект, от 500 000 — сокращённо */
export function formatOfferPriceCell(offer: ICategoryOffer, dealType: TOffersDealType): string {
  if (dealType === 'rent') {
    const rate = getRentRatePerYear(offer);

    return rate === null ? '' : formatRubles(rate);
  }

  if (offer.price === null) {
    return '';
  }

  return offer.price >= CUT_PRICE_FROM ? formatCutPrice(offer.price) : formatRubles(offer.price);
}

/** «Показать все 203 объявления» (`serp_more_link.tsx` монолита: при одном — без «все») */
export function getOffersMoreLinkText(count: number): string {
  const word = plural(count, ['объявление', 'объявления', 'объявлений']);
  const prettyCount = noBreak(numberToPrettyString(count));

  return word === 'объявление' ? `Показать ${prettyCount} ${word}` : `Показать все ${prettyCount} ${word}`;
}
